import { useEffect, useState } from "react";
import {
  verificationCooldownSnapshot,
  verificationCooldownTickDelay,
  type VerificationCooldownSnapshot,
} from "./verification-countdown.js";

/**
 * Live view of the server `retryAfter` deadline. Ticks once per second and
 * re-reads the clock on focus so a throttled background tab cannot keep
 * actions disabled past the real deadline. Never starts its own duration.
 */
export function useVerificationCooldown(retryAfter: string | null): VerificationCooldownSnapshot {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!retryAfter) return;
    let timer = 0;
    const tick = (): void => {
      window.clearTimeout(timer);
      const now = Date.now();
      setNowMs(now);
      const delay = verificationCooldownTickDelay(retryAfter, now);
      if (delay !== null) {
        timer = window.setTimeout(tick, delay);
      }
    };
    const resyncWhenVisible = (): void => {
      if (document.visibilityState === "visible") tick();
    };
    tick();
    document.addEventListener("visibilitychange", resyncWhenVisible);
    window.addEventListener("focus", tick);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", resyncWhenVisible);
      window.removeEventListener("focus", tick);
    };
  }, [retryAfter]);

  return verificationCooldownSnapshot(retryAfter, nowMs);
}
